import { Icon, ICONS_ENUM, Typography } from '@sanlam-glacier/app-common/components';
import styles from './activity-history.module.scss';
import clsx from 'clsx';
import { ActivityStatus, StageGroup } from 'modules/case-tracking/types';
import { useCallback, useState } from 'react';
import { formatDateAsString } from 'utils/string-handling/human-readable-text';
import { Stage } from 'modules/case-tracking/types/case-tracking.enums';

type ActivityHistoryProps = {
  activityHistory?: StageGroup[];
};

type StageGroupId = `${number}-${string}-${string}`;

export const ActivityHistory = ({ activityHistory }: ActivityHistoryProps) => {
  const getInitialExpandedStages = () => {
    if (!activityHistory) return new Set<string>();

    return new Set(
      activityHistory.map((stageGroup, index) => {
        const firstActivityId = stageGroup.activities[0]?.id || '';
        return `${index}-${stageGroup.stage}-${firstActivityId}`;
      })
    );
  };

  const [expandedStages, setExpandedStages] = useState<Set<string>>(() =>
    getInitialExpandedStages()
  );

  const getStageGroupId = (stageGroup: StageGroup, index: number): StageGroupId => {
    const firstActivityId = stageGroup.activities[0]?.id || '';
    return `${index}-${stageGroup.stage}-${firstActivityId}`;
  };

  const handleStageExpansion = useCallback((stageGroupId: StageGroupId) => {
    setExpandedStages((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(stageGroupId)) {
        newExpanded.delete(stageGroupId);
      } else {
        newExpanded.add(stageGroupId);
      }
      return newExpanded;
    });
  }, []);

  const getStageIcon = (stageGroup: StageGroup) => {
    if (stageGroup.isCancelled) {
      return (
        <Icon
          icon={ICONS_ENUM.ALERT_ERROR}
          className={clsx(styles.stageIcon, styles.stageIconCancelled)}
        />
      );
    }
    if (stageGroup.isCompleted) {
      return (
        <Icon
          icon={ICONS_ENUM.ACTION_CHECK}
          className={clsx(styles.stageIcon, styles.stageIconCompleted)}
        />
      );
    }
    if (stageGroup.isInProgress) {
      return <p className={styles.stageCircle}>{stageGroup.stage}</p>;
    }
  };

  const getActivityIcon = (activity: ActivityStatus) => {
    if (activity.isCancelled) {
      return (
        <Icon
          icon={ICONS_ENUM.ALERT_ERROR}
          className={clsx(styles.activityIcon, styles.activityIconCancelled)}
        />
      );
    }
    if (activity.isCompleted) {
      return (
        <Icon
          icon={ICONS_ENUM.ACTION_CHECK}
          className={clsx(styles.activityIcon, styles.activityIconCompleted)}
        />
      );
    }
    return <div className={styles.activityIconPlaceholder} />;
  };

  const sortedStageGroups = activityHistory || [];

  return (
    <div className={styles.activityHistory}>
      <div className={styles.header}>
        <Typography
          className={styles.header}
          data-testid={'activityHistoryHeader'}
          weight={Typography.WEIGHT.NORMAL}
          variant={Typography.VARIANT.B3}
        >
          Recent activity and updates on your instruction
        </Typography>
      </div>

      <div className={styles.timeline}>
        {sortedStageGroups.map((stageGroup, groupIndex) => {
          const stageGroupId: StageGroupId = getStageGroupId(stageGroup, groupIndex);

          return (
            <div
              key={stageGroupId}
              className={styles.stageGroup}
              data-testid={'activityHistoryStageGroup' + stageGroupId}
            >
              <button
                className={clsx(
                  styles.stageHeader,
                  stageGroup.isCancelled && styles.stageHeaderCancelled
                )}
                onClick={() => handleStageExpansion(stageGroupId)}
              >
                <div className={styles.stageHeaderContent}>
                  {getStageIcon(stageGroup)}
                  <Typography
                    className={styles.stageName}
                    data-testid={'activityHistoryStageName'}
                    weight={Typography.WEIGHT.MEDIUM}
                    variant={Typography.VARIANT.B2}
                  >
                    {stageGroup.stageName === Stage.Stage4
                      ? 'Instruction completed'
                      : stageGroup.stageName}
                  </Typography>
                </div>
                <Icon
                  icon={
                    expandedStages.has(stageGroupId)
                      ? ICONS_ENUM.NAVIGATION_CHEVRON_LEFT
                      : ICONS_ENUM.NAVIGATION_CHEVRON_RIGHT
                  }
                  className={styles.chevronIcon}
                />
              </button>

              {expandedStages.has(stageGroupId) && (
                <div
                  className={clsx(
                    styles.stageActivities,
                    expandedStages.has(stageGroupId) && styles.stageActivitiesExpanded
                  )}
                >
                  {stageGroup.activities.map((activity, activityIndex) => (
                    <div
                      key={activity.id}
                      data-testid={`activity-${activity.id}`}
                      className={clsx(
                        styles.activityItem,
                        activity.isCancelled && styles.activityItemCancelled,
                        activity.isInProgress && styles.activityItemInProgress
                      )}
                    >
                      <div className={styles.activityIconContainer}>
                        {activityIndex < stageGroup.activities.length - 1 && (
                          <div
                            className={clsx(
                              styles.connectingLine,
                              activity.isCancelled && styles.connectingLineCancelled
                            )}
                          />
                        )}
                        {groupIndex < sortedStageGroups.length - 1 &&
                          activityIndex === stageGroup.activities.length - 1 && (
                            <div
                              className={clsx(
                                styles.connectingLine,
                                styles.connectingLineToNextStage
                              )}
                            />
                          )}
                      </div>

                      <div className={styles.activityContent}>
                        <div className={styles.activityDetails}>
                          <Typography
                            className={styles.activityName}
                            data-testid={'activityHistoryStageName'}
                            weight={Typography.WEIGHT.NORMAL}
                            variant={Typography.VARIANT.B3}
                            color={Typography.COLOR.SECONDARY}
                          >
                            {activity.statusName + ' '}

                            {activity.isCancelled && activity.cancellationReason && (
                              <span className={styles.cancellationReason}>
                                - {activity.cancellationReason || '-'}
                              </span>
                            )}
                          </Typography>
                        </div>
                        <div className={styles.activityTime}>
                          <Typography
                            className={styles.timestamp}
                            data-testid={'activityHistoryTimeStamp'}
                            weight={Typography.WEIGHT.NORMAL}
                            variant={Typography.VARIANT.B4}
                            color={Typography.COLOR.SECONDARY}
                          >
                            {activity.timestamp}
                          </Typography>
                          <Typography
                            className={styles.date}
                            data-testid={'activityHistoryDate'}
                            weight={Typography.WEIGHT.NORMAL}
                            variant={Typography.VARIANT.B4}
                            color={Typography.COLOR.SECONDARY}
                          >
                            {formatDateAsString(activity.date)}
                          </Typography>
                          {getActivityIcon(activity)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
